import { useEffect, useState } from 'react';

import cn from 'classnames';
import {
  MESSAGE_STATE_ERROR,
  MESSAGE_STATE_INFO,
  MESSAGE_STATE_SUCCESS,
} from 'constants/message-app-state-contants';
import { getAdminRoute } from 'helpers/session';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { Loading } from 'components/Loading/Loading';
import { SessionTimeoutManager } from 'components/SessionTimeoutManager/SessionTimeoutManager';

import { messagePopOut, messagePopUp, selectApp } from 'api/app-slice';
import {
  authenticate,
  logout,
  selectAuthentication,
  stopImpersonation,
} from 'api/authentication-slice';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { getCompanyChecks } from 'api/company-checks-slice';
import { getCompany, selectCompany } from 'api/company-slice';
import { getUserPermissions, selectPermissions } from 'api/user-permissions-slice';
import { getUser, resetUser, selectUser } from 'api/user-slice';

import AppMessage from './AppMessage/AppMessage';
import { AppNavbar } from './AppNavbar/AppNavbar';
import styles from './AppPage.module.scss';
import SupportAccessBanner from './SupportAccessBanner/SupportAccessBanner';

const AppPage = ({
  fillOnScroll,
  loading,
  children,
  requiredPermissions = [],
  hideNavigation = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const authentication = useSelector(selectAuthentication);
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const app = useSelector(selectApp);
  const companyChecks = useSelector(selectCompanyChecks);
  const permissions = useSelector(selectPermissions);

  const [fetchingUser, setFetchingUser] = useState(true);

  let { validate } = useParams();

  useEffect(() => {
    if (validate) {
      dispatch(
        messagePopUp({
          text: 'Your email address has been successfully verified',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    }

    dispatch(authenticate());
  }, []);

  useEffect(() => {
    if (!authentication.checked) return;

    if (!authentication.logged) {
      navigate(`/sign_in?redirect_uri=${window.location.pathname}`);
      return;
    }

    dispatch(getUser(authentication.user_id));
    dispatch(getCompany(authentication.company_id));
    dispatch(getUserPermissions());
    dispatch(getCompanyChecks(authentication.company_id));
  }, [authentication.checked, authentication.user_id]);

  useEffect(() => {
    const expectsUser = authentication.logged && authentication.user_id;
    const userReady = user.id && user.id === authentication.user_id;

    if (expectsUser && !userReady) {
      setFetchingUser(true);
      return;
    }

    if (!userReady) return;

    setFetchingUser(false);

    if (!user.verified) {
      dispatch(
        messagePopUp({
          text: 'Please validate your email address. {{Resend email confirmation}}',
          state: MESSAGE_STATE_ERROR,
          hide: false,
          params: 'SEND_VERIFY_EMAIL',
        }),
      );
    }

    if (authentication.impersonating && user) {
      dispatch(
        messagePopUp({
          text: `You are interpreting ${user.name}. {{Stop interpreting}}`,
          state: MESSAGE_STATE_INFO,
          hide: false,
          params: 'STOP_INTERPRETING',
        }),
      );
    }
  }, [user, authentication.user_id, authentication.logged, authentication.impersonating]);

  useEffect(() => {
    if (!authentication.checked || !authentication.logged) return;
    if (!user.id || user.id !== authentication.user_id) return;

    if (!user.verified && user.overdue) navigate('/account_validation/email');

    if (!authentication.two_factor_auth_enabled && authentication.enforce_two_factor_auth)
      navigate('/account_validation/2fa');
  }, [user, authentication, company, location.pathname]);

  useEffect(() => {
    if (!requiredPermissions || permissions.length < 1) return;

    if (requiredPermissions.filter(permission => !permissions.includes(permission)).length > 0)
      navigate('/dashboard');
  }, [permissions.length, requiredPermissions]);

  const signOut = async () => {
    await dispatch(logout());
    navigate('/sign_in');
  };

  const handleMessageClick = params => {
    switch (params) {
      case 'SEND_VERIFY_EMAIL':
        resendEmail();
        break;
      case 'STOP_INTERPRETING':
        dispatch(resetUser());
        dispatch(messagePopOut());
        dispatch(stopImpersonation())
          .unwrap()
          .then(() => {
            navigate(getAdminRoute());
            window.Intercom?.('shutdown');
          })
          .catch(async () => {
            await dispatch(logout());
            navigate('/sign_in');
          });
        break;
    }
  };

  return (
    <div className={styles.root}>
      <Loading active={loading || fetchingUser}></Loading>
      <AppNavbar
        user={user}
        company={company}
        authentication={authentication}
        companyChecks={companyChecks}
        app={app}
        fillOnScroll={fillOnScroll}
        onSignOut={signOut}
        hideNavigation={hideNavigation}
      />
      <AppMessage {...app.message} onClick={handleMessageClick} />
      {!fetchingUser && <SupportAccessBanner />}
      {authentication.logged && <SessionTimeoutManager />}
      {!fetchingUser && children}
    </div>
  );
};

const Header = ({
  title,
  subtitle,
  className,
  icon,
  ctaText,
  ctaTestId,
  noBorder = false,
  ctaClick = () => {},
}) => (
  <header className={cn(styles.header, { [className]: className, [styles.noBorder]: noBorder })}>
    <div>
      <div className="d-flex align-items-center u-gap">
        {icon && <IconComponent icon={icon} />}
        <div className="u-margin-right">
          <h1 className={styles.title}>{title}</h1>
          <span className="secondary-text u-font-weight-400 u-font-size-small">{subtitle}</span>
        </div>
      </div>
    </div>
    {ctaText && (
      <Button onClick={ctaClick} dataTestId={ctaTestId}>
        {ctaText}
      </Button>
    )}
  </header>
);

AppPage.Header = Header;
export default AppPage;
